import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { eq } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import { variation } from 'ember-launch-darkly';
import {
  HdsPageHeader,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  OperationRefreshRoute,
} from 'core/components';
import { resourceIsBusy, resourceTypeFor, urlFor } from 'core/helpers';
import {
  AWS_CONNECTIONS,
  AWS_CONNECTIONS_INFO,
} from 'networks-common/utils/aws-connections';
import { HVN_ROUTE_NAMES } from 'networks-common/utils/network-routes';
import Container from 'hcp/components/page/hvns/detail/container';
import Table from 'hcp/components/page/hvn-routes/table';

/**
 *
 * `model` is the model sent from a connection type or from the routes tab within a network
 * @argument model;
 * @type {Object}
 *
 * `isHvnHvnPeering` signifies if the routes are foran hvn-hvn peering.
 * @argument isHvnHvnPeering;
 * @type {Boolean}
 *
 */
/**
 *
 * `PageHvnRoutesList` renders the HVN Routes either in a page view or in a table within a connection page view
 *
 *
 * ```
 * <Page::HvnRoutes::List
 *   @model={{@model}}
 *   @isHvnHvnPeering={{true|false}}
 * />
 * ```
 *
 * @class PageHvnRoutesList
 *
 */
export default class PageHvnRoutesList extends Component {
  @service intl;
  @service operation;
  @service router;

  @tracked routeName = this.router.currentRouteName;

  hvnRouteNames = HVN_ROUTE_NAMES;
  connectionsInfo = AWS_CONNECTIONS_INFO;

  get connection() {
    // Check to see if a connection is in the model.
    const { model } = this.args;
    let connection = null;

    AWS_CONNECTIONS.forEach((item) => {
      if (item in model) {
        connection = {
          id: model[item]?.id,
          type: AWS_CONNECTIONS_INFO[item]?.type,
          detail: AWS_CONNECTIONS_INFO[item]?.detail,
          list: AWS_CONNECTIONS_INFO[item]?.list,
          createRoute: AWS_CONNECTIONS_INFO[item]?.createRoute,
          state: model[item]?.state,
        };
      }
    });

    return connection;
  }
  <template>
    {{! Route Table tab view }}
    {{#if (eq this.routeName this.hvnRouteNames.list)}}
      {{! Route Table tab view }}
      <OperationRefreshRoute
        @route={{this.hvnRouteNames.list}}
        @model={{@model.routes}}
        @resourceType={{resourceTypeFor "network-route"}}
        @routeFallback={{urlFor
          "cloud.orgs.detail.projects.detail.hvns.detail"
        }}
      />
      <Container @model={{@model}}>
        <CoreLayoutMain>
          <:header>
            <HdsPageHeader as |PH|>
              <PH.Breadcrumb>
                <BreadcrumbsContainer />
              </PH.Breadcrumb>
              <PH.Title>
                {{t "components.page.hvn-routes.list.title"}}
              </PH.Title>
              {{#unless
                (resourceIsBusy @model.network type=(resourceTypeFor "network"))
              }}
                {{#if (can "create network-route")}}
                  <PH.Actions>
                    <HdsButton
                      @color="primary"
                      @icon={{if (variation "hcp-hvn-guided-flow") "" "plus"}}
                      @iconPosition="leading"
                      @route={{if
                        this.connection
                        this.connection.createRoute
                        this.hvnRouteNames.create
                      }}
                      @text={{t "components.page.hvn-routes.create.title"}}
                      data-test-create
                    />
                  </PH.Actions>
                {{/if}}
              {{/unless}}
            </HdsPageHeader>
          </:header>
          <:body>
            <Table
              @connection={{this.connection}}
              @isHvnHvnPeering={{@isHvnHvnPeering}}
              @model={{@model}}
            />
          </:body>
        </CoreLayoutMain>
      </Container>
    {{else}}
      {{! Target detail view }}
      <OperationRefreshRoute
        @route={{this.connection.detail}}
        @model={{@model.routes}}
        @resourceType={{resourceTypeFor "network-route"}}
        @routeFallback={{urlFor this.connection.list}}
      />
      <Table
        @connection={{this.connection}}
        @isHvnHvnPeering={{@isHvnHvnPeering}}
        @model={{@model}}
      />
    {{/if}}
  </template>
}
